import React from 'react';
import { Text } from '@billda/design-system';
import { BilldaLogo } from '../../atoms/BilldaLogo';
import { AuthBrandedSide } from '../organisms/AuthBrandedSide';
import { AuthFormSide } from '../organisms/AuthFormSide';
import { ThemeToggle } from '../../theme/ThemeToggle';

interface AuthLayoutProps {
  /**
   * Form content to render in the right side
   */
  children: React.ReactNode;
  /**
   * Variant determines the branded side message
   * @default 'login'
   */
  variant?: 'login' | 'invite';
  /**
   * Maximum width of the form container
   * @default 500
   */
  maxWidth?: number;
  /**
   * Organization name to display (for invite variant)
   */
  organizationName?: string;
}

/**
 * Template: Main authentication layout
 * Two-column flexbox layout with branded side and form side
 * Responsive: collapses to single column on mobile
 * Clean, flat design with border-based separation
 */
export const AuthLayout: React.FC<AuthLayoutProps> = ({
  children,
  variant = 'login',
  maxWidth = 500,
  organizationName,
}) => {
  return (
    <div className="relative flex min-h-screen flex-col md:flex-row">
      {/* Theme Toggle - Top Right Corner */}
      <div className="fixed right-4 top-4 z-[1000]">
        <ThemeToggle size="medium" />
      </div>

      {/* Branded Side - Hidden on mobile, 2/3 width on desktop */}
      <div className="relative hidden flex-[2] border-r border-border-default md:flex">
        <AuthBrandedSide
          variant={variant}
          organizationName={organizationName}
        />
      </div>

      {/* Mobile Hero Banner - Only show on mobile for login variant */}
      {variant === 'login' && (
        <div
          className="flex flex-col items-center justify-center gap-2.5 px-8 pb-8 pt-10 md:hidden"
          style={{
            background:
              'linear-gradient(160deg, var(--color-surface-inverse-active) 0%, var(--color-surface-inverse) 60%, var(--color-surface-inverse-hover) 100%)',
          }}
        >
          <BilldaLogo variant="secondary" width={120} />
          <Text
            variant="body2"
            className="font-semibold tracking-[0.02em] text-surface-primary"
          >
            From site to payday.
          </Text>
        </div>
      )}

      {/* Form Side - Full width on mobile, 1/3 on desktop */}
      <div className="relative flex flex-1 flex-col items-center bg-surface-canvas pt-4 justify-start md:justify-center md:pt-0">
        {/* Form Content */}
        <AuthFormSide maxWidth={maxWidth}>{children}</AuthFormSide>
      </div>
    </div>
  );
};
